import { WitzyLogo } from '@components/branding/WitzyLogo';
import { cn } from '@lib/utils';
import { SparkleOrb } from '@ui/index';
import { MessageCircle, Shield, Zap } from 'lucide-react';
import { Outlet } from 'react-router-dom';



const FEATURES = [
  {
    icon: MessageCircle,
    title: 'Shared inbox',
    description: 'Every customer conversation in one place.',
    gradient: 'from-blue-400 to-indigo-500',
    glow: 'rgba(96,165,250,0.30)',
  },
  {
    icon: Zap,
    title: 'Campaign engine',
    description: 'Send to thousands. Pay only for what you use.',
    gradient: 'from-amber-400 to-orange-500',
    glow: 'rgba(251,191,36,0.30)',
  },
  {
    icon: Shield,
    title: 'Enterprise security',
    description: 'AES-256 encryption, RBAC, audit logs.',
    gradient: 'from-emerald-400 to-teal-500',
    glow: 'rgba(52,211,153,0.30)',
  },
];

/**
 * Split-panel auth layout per spec §9.5.
 *
 * Left (55%): sparkle gradient background + brand identity + feature cards.
 * Right (45%): pure-white, centered form content (<Outlet />).
 * Mobile: left panel hidden, right panel full width.
 */
export function AuthLayout(): JSX.Element {
  return (
    <div className="flex min-h-screen">
      {/* Left panel — hidden on mobile */}
      <div className="relative hidden w-[55%] overflow-hidden bg-gradient-aurora lg:flex lg:flex-col lg:items-center lg:justify-center lg:px-12 xl:px-20">
        <SparkleOrb color="green" size="xl" className="-top-32 -left-32" />
        <SparkleOrb color="purple" size="lg" className="bottom-20 right-0" />
        <SparkleOrb color="blue" size="md" className="top-1/3 right-1/4" />

        <div className="relative max-w-md">
          {/* Logo. On a white chip rather than tone="white": the mark's green
              "WA" badge holds white text, so flattening the artwork to a
              single colour erases the badge into a blank rectangle. */}
          <div className="mb-8 inline-flex rounded-2xl bg-white/95 px-5 py-3 shadow-sm backdrop-blur-sm">
            <WitzyLogo textSize={26} />
          </div>

          <h2 className="mb-3 text-[36px] font-bold leading-tight tracking-apple-tight text-white">
            WhatsApp Business,
            <br />
            done right.
          </h2>
          <p className="mb-10 text-[16px] leading-relaxed text-white/80">
            The modern platform for marketing, support, and automation —
            built for teams that refuse to settle.
          </p>

          {/* Feature cards */}
          <div className="space-y-3">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="flex items-start gap-4 rounded-2xl bg-white/12 px-5 py-4 backdrop-blur-sm"
              >
                <span
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white bg-gradient-to-br',
                    f.gradient,
                  )}
                  style={{ boxShadow: `0 4px 12px ${f.glow}` }}
                >
                  <f.icon size={18} strokeWidth={1.5} />
                </span>
                <div>
                  <div className="text-[14px] font-semibold text-white">{f.title}</div>
                  <div className="mt-0.5 text-[13px] text-white/70">{f.description}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Social proof */}
          <div className="mt-10 rounded-xl bg-white/10 px-5 py-3 backdrop-blur-sm">
            <p className="text-[13px] italic leading-relaxed text-white/80">
              &ldquo;We switched from Wati and haven&apos;t looked back. Witzy&apos;s
              inbox alone saved us 4 hours a day.&rdquo;
            </p>
            <p className="mt-2 text-[12px] font-semibold text-white/90">
              — Priya, Head of Growth at Acme
            </p>
          </div>
        </div>
      </div>

      {/* Right panel — form content */}
      <div className="flex w-full flex-col items-center justify-center bg-white px-6 py-12 lg:w-[45%] lg:px-12 xl:px-20">
        {/* Mobile logo */}
        <div className="mb-8 flex items-center gap-2 lg:hidden">
          <WitzyLogo textSize={20} />
        </div>

        <div className="w-full max-w-sm">
          <Outlet />
        </div>

        {/* Bottom text */}
        <p className="mt-10 text-[12px] text-apple-gray-500">
          &copy; {new Date().getFullYear()} Witzy. All rights reserved.
        </p>
      </div>
    </div>
  );
}
